import React from 'react';
import { motion } from 'framer-motion';

const ShoppingDetoxLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        30-Day Shopping Detox Plan: Break Your Shopping Addiction
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Your Intensive Shopping Intervention</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Take control with our structured 30-day program designed for serious shopping issues. Break compulsive buying patterns and rebuild a healthier relationship with spending.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Shopping Detox
            </motion.button>
          </div>
          <div className="text-8xl flex-shrink-0">🛑</div>
        </div>
      </div>
      
      {/* Signs You Need A Shopping Detox */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Signs You Need A Shopping Detox</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💳</span>
              <div>
                <h3 className="font-bold dark:text-white">Financial Distress</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Shopping has led to credit card debt, depleted savings, or difficulty paying essential bills.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🤫</span>
              <div>
                <h3 className="font-bold dark:text-white">Secret Shopping</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  You hide purchases, lie about spending, or have packages delivered in secret.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😰</span>
              <div>
                <h3 className="font-bold dark:text-white">Withdrawal Symptoms</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  You feel anxious, restless, or irritable when unable to shop or browse online stores.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📦</span>
              <div>
                <h3 className="font-bold dark:text-white">Unused Purchases</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Your home contains many items still in packaging or with tags attached.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🔄</span>
              <div>
                <h3 className="font-bold dark:text-white">Failed Control Attempts</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  You've tried to stop or reduce shopping multiple times without success.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👥</span>
              <div>
                <h3 className="font-bold dark:text-white">Relationship Impact</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Shopping has caused arguments, trust issues, or isolation from loved ones.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* How Our Program Works */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">The 30-Day Shopping Detox Program</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Complete Shopping Fast (Days 1-7)
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Begin with a total shopping freeze. No purchases except absolute necessities like groceries and medications.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Digital Declutter (Days 8-14)
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Remove shopping apps, unsubscribe from retail emails, block shopping websites, and unfollow brands on social media.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Trigger Identification & Management (Days 15-21)
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Track emotional states that drive shopping urges and develop alternative coping mechanisms for each trigger.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Mindful Spending Reintroduction (Days 22-30)
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Gradually reintroduce planned, intentional purchases while practicing new decision-making frameworks.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Program Features */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">What You'll Get</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📱</span>
              <h3 className="font-bold mt-2 dark:text-white">Daily Accountability</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Daily check-ins, progress tracking, and celebration of no-spend streaks to keep you motivated.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📊</span>
              <h3 className="font-bold mt-2 dark:text-white">Enhanced Analytics</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Advanced emotional and financial pattern tracking to identify your unique shopping addiction triggers.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📝</span>
              <h3 className="font-bold mt-2 dark:text-white">Guided Exercises</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Daily reflections, cognitive behavioral techniques, and specialized activities to rewire shopping habits.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📚</span>
              <h3 className="font-bold mt-2 dark:text-white">Resource Library</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Expert articles, worksheets, and tools based on addiction recovery and financial psychology.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⚡</span>
              <h3 className="font-bold mt-2 dark:text-white">Urge Management Tools</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Instant techniques to overcome shopping urges when they strike, including guided interventions.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🛣️</span>
              <h3 className="font-bold mt-2 dark:text-white">Post-Detox Plan</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Customized maintenance strategy to prevent relapse and maintain healthy shopping habits long-term.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Shopping Addiction Stats */}
      <motion.section 
        className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 dark:text-white">Shopping Addiction Reality Check</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">5.8%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of adults experience compulsive buying disorder</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$5,400</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average annual debt from shopping addiction</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">84%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">success rate with structured intervention programs</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">92%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">report improved financial wellness after detox</p>
          </div>
        </div>
      </motion.section>
      
      {/* Program Components Visualization */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Your 30-Day Journey</h2>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="space-y-8">
            <div>
              <div className="relative">
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                  <div className="h-2 bg-blue-500 rounded-full" style={{ width: "25%" }}></div>
                </div>
                <div className="absolute top-0 left-[25%] transform -translate-x-1/2 -translate-y-full mb-2">
                  <div className="bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    7
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                <h3 className="font-bold text-sm dark:text-white">Phase 1: Complete Shopping Fast</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Break the habit cycle with a total shopping freeze. Learn to distinguish wants from needs.
                </p>
              </div>
            </div>
            
            <div>
              <div className="relative">
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                  <div className="h-2 bg-purple-500 rounded-full" style={{ width: "50%" }}></div>
                </div>
                <div className="absolute top-0 left-[50%] transform -translate-x-1/2 -translate-y-full mb-2">
                  <div className="bg-purple-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    14
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <h3 className="font-bold text-sm dark:text-white">Phase 2: Digital Declutter</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Remove shopping temptations from your environment and create friction between urges and purchases.
                </p>
              </div>
            </div>
            
            <div>
              <div className="relative">
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                  <div className="h-2 bg-pink-500 rounded-full" style={{ width: "75%" }}></div>
                </div>
                <div className="absolute top-0 left-[75%] transform -translate-x-1/2 -translate-y-full mb-2">
                  <div className="bg-pink-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    21
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg p-3">
                <h3 className="font-bold text-sm dark:text-white">Phase 3: Trigger Management</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Develop a personalized trigger map and alternative response plan for emotional shopping urges.
                </p>
              </div>
            </div>
            
            <div>
              <div className="relative">
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                  <div className="h-2 bg-green-500 rounded-full" style={{ width: "100%" }}></div>
                </div>
                <div className="absolute top-0 left-[100%] transform -translate-x-1/2 -translate-y-full mb-2">
                  <div className="bg-green-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    30
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                <h3 className="font-bold text-sm dark:text-white">Phase 4: Mindful Reintroduction</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Learn sustainable shopping practices and create a framework for intentional purchasing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Testimonials section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Success Stories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I was $23,000 in credit card debt from online shopping. After completing the 30-day detox, I not only stopped accumulating debt but developed a plan to pay it off. For the first time in years, I feel in control."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jennifer K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">4 months shopping-sober</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I was spending 3+ hours daily browsing online stores and hiding packages from my partner. The detox program helped me identify that I shopped when anxious. Now I have healthier coping mechanisms and my relationship is healing."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Marcus T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Completed detox 6 months ago</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* FAQ Section - important for SEO */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is this program right for me?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              This intensive detox is designed for those experiencing serious shopping issues—financial problems from shopping, hiding purchases, feeling unable to stop, or experiencing anxiety when not shopping. If shopping is negatively impacting your finances, relationships, or mental wellbeing, this program can help.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Will I never be able to shop again?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The goal isn't to eliminate shopping permanently, but to break compulsive patterns and build a healthier relationship with spending. The final phase focuses on reintroducing mindful, intentional shopping based on needs and values rather than emotional impulses.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Can this replace professional therapy?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              While our program incorporates evidence-based techniques, it isn't a substitute for professional mental health treatment. For severe shopping addiction with underlying issues like anxiety, depression, or trauma, we recommend using our program alongside professional support.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What happens after the 30 days?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              You'll receive a personalized maintenance plan based on your progress and specific triggers. This includes ongoing tools, check-ins, and strategies to prevent relapse. Many users continue tracking purchases with our standard tools after completing the intensive program.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* CTA section */}
      <motion.section 
        className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Break Free from Shopping Addiction?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start your 30-day detox journey today and transform your relationship with shopping.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Begin Your Shopping Detox
        </motion.button>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
          All program data remains private and stored only on your device.
        </p>
      </motion.section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Shopping Addiction and Recovery</h2>
          <p className="mb-3">
            Shopping addiction, or compulsive buying disorder, is characterized by an uncontrollable urge to shop despite negative consequences. This behavior is often used to temporarily relieve negative emotions like anxiety, loneliness, or low self-esteem.
          </p>
          <p className="mb-3">
            Our 30-Day Shopping Detox provides a structured intervention incorporating elements of cognitive behavioral therapy, habit reversal training, and financial psychology. The program addresses both the behavioral patterns and emotional triggers that drive compulsive shopping.
          </p>
          <p>
            Whether you're dealing with occasional shopping binges or a more severe addiction that's affecting your finances and relationships, this intensive program offers a comprehensive approach to recovery and sustainable behavior change.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default ShoppingDetoxLanding;
